import { useCallback, useEffect, useRef } from 'react';

export function usePhotoModerationKeyboard(
  onApprove: () => void,
  onReject: () => void,
  onClose: () => void,
  enabled: boolean,
) {
  const debounceRef = useRef(false);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled || debounceRef.current) return;

      // Don't hijack keystrokes from typing surfaces — inputs, textareas,
      // selects, and contenteditable are likely consuming the key themselves.
      const target = event.target as HTMLElement | null;

      if (target) {
        const tag = target.tagName;

        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }

      switch (event.key) {
        case 'ArrowRight':
        case 'd':
        case 'D':
          event.preventDefault();
          debounceRef.current = true;
          onApprove();
          setTimeout(() => {
            debounceRef.current = false;
          }, 500);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          event.preventDefault();
          debounceRef.current = true;
          onReject();
          setTimeout(() => {
            debounceRef.current = false;
          }, 500);
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    },
    [enabled, onApprove, onReject, onClose],
  );

  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);

      return () => document.removeEventListener('keydown', handleKeyDown);
    }

    return undefined;
  }, [enabled, handleKeyDown]);
}
